import { useBgFormContext } from "@app/guranteeFormWrapper";
import { isCooperative, setShowPurchaseFields } from "@app/store/purchaseSlice";
import { BgFormInput } from "@utils/guaranteeForm/formShema";
import { useCallback, useEffect, useState } from "react";
import { FieldErrors, useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import { LOT_MODEL } from "@utils/purchaseLotModels";
import { PURCHASE_MAP_ERROR_TYPE, PURCHASE_MAP_ERRORS } from "@utils/purchaseErrors";
import { purchaseMap } from "@utils/mapPurchaseFromSeldon";
import { getGuaranteePurchase } from "@services/guarantees";

type UseSearchProps = {
  currentPrefix: string;
};

const ALLOWED_BENEFICIARY_FIELDS = new Set(["customerRegNum", "phone", "email", "bgAmount"]);
type PathSeg = string | number;

function flattenErrorPaths(errors: FieldErrors<BgFormInput>): PathSeg[][] {
  const out: PathSeg[][] = [];
  (function walk(node: unknown, base: PathSeg[]) {
    if (!node || typeof node !== "object") return;
    if ("message" in node || "type" in node) {
      out.push(base);
      return;
    }
    for (const k of Object.keys(node)) {
      if (k === "ref" || k === "types") continue;
      const seg: PathSeg = /^\d+$/.test(k) ? Number(k) : k;
      walk(node[k], [...base, seg]);
    }
  })(errors, []);
  return out;
}

export function shouldShowFullFormByErrors(errors: FieldErrors<BgFormInput>): boolean {
  if (!errors || Object.keys(errors).length === 0) return false;

  const paths = flattenErrorPaths(errors);

  for (const path of paths) {
    // 1) находим "purchase" в пути (префикс может быть)
    const pIdx = path.findIndex((s) => s === "purchase");
    if (pIdx === -1) {
      // Ошибка не в purchase.* — игнорируем для этой задачи
      continue;
    }

    // 2) Если ошибка прямо под purchase (напр. purchase.marketPlace) — раскрываем всё
    if (path.length === pIdx + 2 && typeof path[pIdx + 1] === "string") {
      return true;
    }

    // 3) Проверяем случай с beneficiaries: purchase.lots.[i].beneficiaries.[j].<field>(.something?)
    const isLots = path[pIdx + 1] === "lots" && typeof path[pIdx + 2] === "number";
    const isBen =
      isLots && path[pIdx + 3] === "beneficiaries" && typeof path[pIdx + 4] === "number";

    if (isBen) {
      const field = path[pIdx + 5]; // "email" | "phone" | "customerRegNum" | "bgAmount" | ...
      if (typeof field === "string" && ALLOWED_BENEFICIARY_FIELDS.has(field)) {
        // Разрешённые поля бенефициара — пропускаем
        continue;
      }
      // Любое иное поле у бенефициара (или ошибка на массиве/объекте целиком) — раскрываем всё
      return true;
    }

    // 4) Любая другая ошибка внутри purchase (lots.*, ikzList, products, contractConditions и т.п.)
    return true;
  }

  // До сюда дошли — в purchase только разрешённые ошибки бенефициара
  return false;
}

export const usePurchaseSearch = ({ currentPrefix }: UseSearchProps) => {
  const dispatch = useDispatch();
  const methods = useFormContext<BgFormInput>();
  const { setError, clearErrors, formState, getValues, reset, trigger } = methods;
  const { prefix, isStartForm } = useBgFormContext();

  const isCooperativePurchaseAllowed = useSelector(isCooperative);

  const [isSearch, setIsSearch] = useState(false);
  const [canSelectLot, setCanSelectLot] = useState(false);
  const [tempPurchase, setTempPurchase] = useState<Record<string, unknown> | null>(null);

  const [foundedPurchase, setFoundedPurchase] = useState<Record<string, unknown> | null>(null);

  const [purchaseSearchErr, setPurchaseSearchErr] = useState(false);

  const processSearchError = (isError) => {
    setPurchaseSearchErr(isError);

    if (isError) {
      setError("purchaseSearch" as "purchase", {
        type: "custom",
        message: "Закупка не найдена",
      });
    } else {
      clearErrors("purchaseSearch" as "purchase");
    }
  };

  const onPurchaseNumberChange = useCallback(
    debounce(async (value: string) => {
      if (!reset) return;
      clearErrors();

      dispatch(setShowPurchaseFields(false));
      processSearchError(false);
      setFoundedPurchase(null);
      setTempPurchase(null);

      const purchaseNumber = value;
      reset(
        {
          ...getValues(),
          purchase: {
            purchaseNumber,
            lots: [LOT_MODEL],
          },
        },
        { keepErrors: false }
      );

      if (formState.errors[PURCHASE_MAP_ERROR_TYPE.purchaseMapError]) {
        clearErrors(PURCHASE_MAP_ERROR_TYPE.purchaseMapError as "purchase");
      }

      await onSearchPurchase(purchaseNumber);
    }, 500),
    [reset, dispatch, currentPrefix, clearErrors, getValues, formState.errors]
  );

  const getIsCooperative = (lots) => {
    const uniqBens = new Set<number>();
    if (!isCooperativePurchaseAllowed) {
      lots.forEach((lot) => {
        lot.customersList.forEach((customer) => {
          uniqBens.add(customer.organization.id);
        });
      });

      if (uniqBens.size > 1) {
        setError(PURCHASE_MAP_ERROR_TYPE.purchaseMapError as "purchase", {
          type: "custom",
          message: PURCHASE_MAP_ERRORS[PURCHASE_MAP_ERROR_TYPE["cooperative"]],
        });

        return false;
      }
    }

    return true;
  };

  const mapPurchaseToForm = (purchase: Record<string, unknown>) => {
    const mappedPurchase = purchaseMap(purchase);
    setFoundedPurchase(mappedPurchase);

    setTempPurchase(null);
    setIsSearch(false)
    // dispatch(setModalFetching(false));
    // dispatch(setShowPurchaseFields(true));
  };

  const onSearchPurchase = async (number: string) => {
    if (!getValues) return;
    setIsSearch(true);
    // dispatch(setModalFetching(true));

    try {
      const purchase = await getGuaranteePurchase(number);

      if (purchase.length === 0) {
        setIsSearch(false)
        // dispatch(setModalFetching(false));
        processSearchError(true);

        return;
      }

      clearErrors("purchaseSearch" as "purchase");
      setTempPurchase(purchase);

      if (purchase.lotsList.length > 1) {
        if (!getIsCooperative(purchase.lotsList)) {
          setIsSearch(false)
          // dispatch(setModalFetching(false));
          return;
        }

        setCanSelectLot(true);
        return;
      }

      if (!getIsCooperative(purchase.lotsList)) {
        setIsSearch(false)
        // dispatch(setModalFetching(false));
        return;
      }

      mapPurchaseToForm(purchase);
    } catch (_) {
      setIsSearch(false)
      // dispatch(setModalFetching(false));
      processSearchError(true);
      return;
    }
  };

  const onCompleteSelectLots = (lots) => {
    if (!tempPurchase) return;

    tempPurchase.lotsList = lots;
    mapPurchaseToForm(tempPurchase);

    setCanSelectLot(false);
  };

  const onCloseSelectLots = () => {
    setCanSelectLot(false);
    setTempPurchase(null);
    setIsSearch(false)
    // dispatch(setModalFetching(false));
    dispatch(setShowPurchaseFields(false));

    if (!reset) return;

    reset({
      ...getValues(),
      purchase: {
        lots: [LOT_MODEL],
      },
    });
  };

  const handleManualInput = () => {
    dispatch(setShowPurchaseFields(true));
    processSearchError(false);
  };

  useEffect(() => {
    if (!isStartForm) return;
    if (!foundedPurchase) return;
    if (!reset || !getValues) return;

    const purchaseObj = prefix
      ? { [prefix]: { purchase: { ...foundedPurchase } } }
      : { purchase: { ...foundedPurchase } };

    reset(
      {
        ...getValues(),
        ...purchaseObj,
      },
      { keepErrors: false }
    );

    const checkForm = async () => {
      await trigger();
    };

    checkForm();
  }, [foundedPurchase]);

  useEffect(() => {
    const showAll = shouldShowFullFormByErrors(methods.formState.errors);
    if (showAll) {
      dispatch(setShowPurchaseFields(true));
    }
  }, [methods.formState.errors]);

  return {
    tempPurchase,
    canSelectLot,
    onPurchaseNumberChange,
    onCompleteSelectLots,
    onCloseSelectLots,
    purchaseSearchErr,
    handleManualInput,
    isSearch
  };
};
